import { notify } from "@general-backoffice/core/index";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";

import { subscriptionsEndpoints } from "../../api";
import getErrorMessage from "../../utils/getErrorMessage";

interface CancelSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  subscription?: { id?: number | string; [key: string]: unknown } | null;
  bot?: {
    name?: string;
    exchange_account?: { exchange?: { name?: string } | null; [key: string]: unknown } | null;
    [key: string]: unknown;
  } | null;
  onSuccess?: () => void;
}

const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({
  isOpen,
  onClose,
  subscription,
  bot,
  onSuccess,
}) => {
  const { t } = useTranslation("dashboard", {
    keyPrefix: "exchangeAccount.cancelSubscriptionModal",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const botName = bot?.name || bot?.exchange_account?.exchange?.name || t("thisBot");

  const handleClose = useCallback(() => {
    if (!isLoading) {
      setError(null);
      onClose();
    }
  }, [isLoading, onClose]);

  const handleConfirm = async () => {
    if (!subscription?.id || isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      await subscriptionsEndpoints.cancelSubscription(subscription.id);
      notify.success(t("success"));
      handleClose();
      onSuccess?.();
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err, t("error") || "Error cancelling subscription");
      setError(errorMessage);
      notify.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered className="cancel-subscription-modal" size="md">
      <ModalHeader toggle={handleClose} className="border-0 pb-0">
        <div className="d-flex align-items-center">
          <div className="modal-icon-wrapper mr-3">
            <i className="fas fa-ban" />
          </div>
          <div>
            <h5 className="mb-0 font-weight-bold">{t("title")}</h5>
          </div>
        </div>
      </ModalHeader>

      <ModalBody className="pt-4">
        {/* Warning Box */}
        <div className="cancel-warning-box">
          <div className="cancel-warning-icon">
            <i className="fas fa-exclamation-triangle" />
          </div>
          <div className="cancel-warning-content">
            <div className="cancel-warning-title">{t("warningTitle")}</div>
            <div className="cancel-warning-text">{t("warningMessage", { botName })}</div>
          </div>
        </div>

        {/* What happens section */}
        <div className="consequences-box mt-4">
          <div className="consequences-title">{t("consequencesTitle")}</div>
          <ul className="consequences-list mb-0">
            <li>
              <i className="fas fa-pause-circle text-warning mr-2" />
              {t("consequence1")}
            </li>
            <li>
              <i className="fas fa-credit-card text-danger mr-2" />
              {t("consequence2")}
            </li>
            <li>
              <i className="fas fa-exchange-alt text-info mr-2" />
              {t("consequence3")}
            </li>
          </ul>
        </div>

        {/* Error Display */}
        {error && (
          <Alert color="danger" className="mt-3 mb-0">
            <i className="fas fa-times-circle mr-2" />
            {error}
          </Alert>
        )}
      </ModalBody>

      <ModalFooter className="border-0 pt-0">
        <Button color="light" onClick={handleClose} disabled={isLoading} className="btn-cancel">
          {t("cancel")}
        </Button>
        <Button color="danger" onClick={handleConfirm} disabled={isLoading} className="btn-confirm-cancel">
          {isLoading ? (
            <>
              <Spinner size="sm" className="mr-2" />
              {t("cancelling")}
            </>
          ) : (
            <>
              <i className="fas fa-ban mr-2" />
              {t("confirm")}
            </>
          )}
        </Button>
      </ModalFooter>

      <style>{`
        .cancel-subscription-modal .modal-content {
          border: none;
          border-radius: 12px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          overflow: hidden;
        }

        .cancel-subscription-modal .modal-header {
          padding: 1.5rem 1.5rem 0.5rem;
        }

        .cancel-subscription-modal .modal-icon-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: linear-gradient(135deg, #f5365c 0%, #f56036 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.25rem;
        }

        .cancel-subscription-modal .modal-body {
          padding: 1rem 1.5rem 1.5rem;
        }

        .cancel-subscription-modal .modal-footer {
          padding: 1rem 1.5rem 1.5rem;
          gap: 0.75rem;
        }

        /* Warning Box */
        .cancel-warning-box {
          display: flex;
          gap: 1rem;
          padding: 1rem;
          background: linear-gradient(135deg, rgba(245, 54, 92, 0.08) 0%, rgba(245, 96, 54, 0.08) 100%);
          border-radius: 10px;
          border: 1px solid rgba(245, 54, 92, 0.2);
        }

        .cancel-warning-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: linear-gradient(135deg, #f5365c 0%, #f56036 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1rem;
          flex-shrink: 0;
        }

        .cancel-warning-content {
          flex: 1;
        }

        .cancel-warning-title {
          font-weight: 600;
          color: #f5365c;
          font-size: 0.875rem;
          margin-bottom: 0.25rem;
        }

        .cancel-warning-text {
          font-size: 0.8125rem;
          color: #525f7f;
          line-height: 1.5;
        }

        /* Consequences Box */
        .consequences-box {
          background: #f8f9fa;
          border-radius: 10px;
          padding: 1rem;
          border: 1px solid #e9ecef;
        }

        .consequences-title {
          font-weight: 600;
          color: #32325d;
          font-size: 0.875rem;
          margin-bottom: 0.75rem;
        }

        .consequences-list {
          list-style: none;
          padding: 0;
        }

        .consequences-list li {
          padding: 0.5rem 0;
          font-size: 0.8125rem;
          color: #525f7f;
          border-bottom: 1px solid #e9ecef;
        }

        .consequences-list li:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }

        /* Buttons */
        .btn-cancel {
          padding: 0.625rem 1.25rem;
          border-radius: 8px;
          font-weight: 500;
        }

        .btn-confirm-cancel {
          padding: 0.625rem 1.5rem;
          border-radius: 8px;
          font-weight: 500;
          background: linear-gradient(135deg, #f5365c 0%, #f56036 100%);
          border: none;
          box-shadow: 0 4px 6px rgba(245, 54, 92, 0.25);
          transition: all 0.2s ease;
        }

        .btn-confirm-cancel:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 7px 14px rgba(245, 54, 92, 0.3);
        }

        .btn-confirm-cancel:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </Modal>
  );
};

export default CancelSubscriptionModal;
